import React from 'react';
import { formatDate, formatRelative } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import { Alert } from '../user-dashboard/UserDashboard';
import './AlertList.css';

interface AlertListProps {
  alerts: Alert[];
  selectedAlertId?: string;
  onSelect: (alert: Alert) => void;
  onAcknowledge: (alertId: string) => void;
  onClose: (alertId: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export const AlertList: React.FC<AlertListProps> = ({
  alerts,
  selectedAlertId,
  onSelect,
  onAcknowledge,
  onClose,
  hasMore,
  onLoadMore,
  loadingMore,
}) => {
  useDateTimePreferences();
  if (alerts.length === 0) {
    return (
      <div className="alert-list empty">
        <p>No alerts found</p>
      </div>
    );
  }

  const getSeverityClass = (severity: string) => {
    return `severity-${severity.toLowerCase()}`;
  };

  const getStatusClass = (status: string) => {
    return `status-${status.toLowerCase()}`;
  };

  const getPriorityClass = (priority: string) => {
    return `priority-${(priority || 'p3').toLowerCase()}`;
  };

  const formatTime = (timestamp: string) => {
    return Math.abs(Date.now() - new Date(timestamp).getTime()) < 86400000 ? formatRelative(timestamp) : formatDate(timestamp);
  };

  const safeStatus = (status: string) => String(status || '').toLowerCase();
  const safePriority = (priority?: string) => (priority || 'P3').toUpperCase();

  const counts = {
    total: alerts.length,
    open: alerts.filter((a) => safeStatus(a.status) !== 'closed').length,
    critical: alerts.filter((a) => safePriority((a as any).priority) === 'P1').length,
    high: alerts.filter((a) => safePriority((a as any).priority) === 'P2').length,
  };

  return (
    <div className="alert-list">
      <div className="alert-list-header">
        <span className="count">{counts.total} alerts</span>
        <div className="list-kpis">
          <span className="list-kpi">Open {counts.open}</span>
          <span className="list-kpi">P1 {counts.critical}</span>
          <span className="list-kpi">P2 {counts.high}</span>
        </div>
      </div>
      <div className="alert-items">
        {alerts.map((alert) => (
          <div
            key={alert.alertId}
            className={`alert-item ${selectedAlertId === alert.alertId ? 'selected' : ''}`}
            onClick={() => onSelect(alert)}
          >
            <div className="alert-item-main">
              <div className="alert-item-header">
                <span className={`badge severity ${getSeverityClass(alert.severity)}`}>
                  {alert.severity}
                </span>
                <span className={`badge status ${getStatusClass(alert.status)}`}>
                  {alert.status}
                </span>
                {alert.priority && (
                  <span className={`badge priority ${getPriorityClass(alert.priority)}`}>
                    {alert.priority}
                  </span>
                )}
              </div>
              <h4 className="alert-title">{alert.title}</h4>
              <div className="alert-meta">
                <span className="service">{alert.service}</span>
                <span className="separator">•</span>
                <span className="source">{alert.source}</span>
              </div>
              {alert.externalTicketId && (
                <span className="ticket-number" title={`Ticket #${alert.externalTicketId}`}>
                  Ticket #{alert.externalTicketId}
                </span>
              )}
            </div>
            <div className="alert-item-actions">
              <span className="time">{formatTime(alert.createdAt)}</span>
              {safeStatus(alert.status) !== 'closed' && (
                <div className="quick-actions">
                  <button
                    className="btn-quick-ack"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAcknowledge(alert.alertId);
                    }}
                    title="Acknowledge"
                  >
                    ✓
                  </button>
                  <button
                    className="btn-quick-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(alert.alertId);
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {hasMore && onLoadMore && (
        <div className="alert-list-footer">
          <button className="btn-load-more" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};
